import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { apiService } from '../../api-base'
import {
  MarketingOrdersResponse,
  MarketingReportStatus,
  SalesData,
  Slider,
  UpdateSalePayload,
  AvailabilityProduct,
  CouponsResponse,
  CouponPayload,
  UpdateCouponPayload,
  Sale,
  SalePayload,
  CreateSlidePayload,
  CreateSliderPayload,
  SlidePayload,
  SliderPayload,
} from './types'
import { Category, Product } from '../products/types'

export class MarketingService {
  static getSales(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<SalesData>> {
    return apiService.get<AxiosResponse<SalesData>>(`/sales`, config)
  }

  static getSliders(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Slider[]>> {
    return apiService.get<AxiosResponse<Slider[]>>(`/sliders`, config)
  }

  static getProductsForFilter(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Product[]>> {
    return apiService.get<AxiosResponse<Product[]>>(`/products`, config)
  }

  static getProductsForForm(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<AvailabilityProduct[]>> {
    return apiService.get<AxiosResponse<AvailabilityProduct[]>>(
      `/products/availabilities`,
      config,
    )
  }

  static getCategories(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<Category[]>> {
    return apiService.get<AxiosResponse<Category[]>>(`/categories`, config)
  }

  static getCoupons(
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<CouponsResponse>> {
    return apiService.get<AxiosResponse<CouponsResponse>>(`/coupons`, config)
  }

  static getCouponOrders(
    id: string,
  ): Promise<AxiosResponse<MarketingOrdersResponse>> {
    return apiService.get<AxiosResponse<MarketingOrdersResponse>>(
      `/coupons/${id}/orders`,
    )
  }

  static getSaleOrders(
    id: string,
  ): Promise<AxiosResponse<MarketingOrdersResponse>> {
    return apiService.get<AxiosResponse<MarketingOrdersResponse>>(
      `/sales/${id}/orders`,
    )
  }

  static createCouponOrdersReport(
    id: string,
  ): Promise<AxiosResponse<MarketingReportStatus>> {
    return apiService.post(`/coupons/${id}/orders/report`)
  }

  static createSaleOrdersReport(
    id: string,
  ): Promise<AxiosResponse<MarketingReportStatus>> {
    return apiService.post(`/sales/${id}/orders/report`)
  }

  static getCouponOrdersReportStatus(
    id: string,
  ): Promise<AxiosResponse<MarketingReportStatus>> {
    return apiService.get(`/coupons/${id}/orders/report`)
  }

  static getSaleOrdersReportStatus(
    id: string,
  ): Promise<AxiosResponse<MarketingReportStatus>> {
    return apiService.get(`/sales/${id}/orders/report`)
  }

  static updateSale(sale: UpdateSalePayload, path: 'product' | 'category') {
    return apiService.put<AxiosResponse<Sale>>(`/sales/${path}/${sale.id}`, {
      sale,
    })
  }

  static updateSlider(data: SliderPayload): Promise<AxiosResponse> {
    return apiService.put<AxiosResponse>(`/sliders/${data.id}`, data)
  }

  static updateSlide(data: SlidePayload, id: string): Promise<AxiosResponse> {
    return apiService.put<AxiosResponse>(
      `/sliders/${data.id}/slides/${id}`,
      data,
    )
  }

  static updateCoupon(coupon: UpdateCouponPayload) {
    return apiService.put(`/coupons/${coupon.id}`, {
      coupon,
    })
  }

  static createSale(data: SalePayload, path: 'product' | 'category' | 'all') {
    return apiService.post(`/sales/${path}`, {
      sale: data,
    })
  }

  static createCoupon(data: CouponPayload) {
    return apiService.post(`/coupons`, {
      coupon: data,
    })
  }

  static createSlider(data: CreateSliderPayload): Promise<AxiosResponse> {
    return apiService.post(`/sliders`, data)
  }

  static createSlide(
    data: CreateSlidePayload,
    id: string,
  ): Promise<AxiosResponse> {
    return apiService.post(`/sliders/${id}/slides`, data)
  }

  static deleteSale(
    id: string,
    path: 'product' | 'category',
  ): Promise<AxiosResponse<SalesData>> {
    return apiService.delete<AxiosResponse<SalesData>>(`/sales/${path}/${id}`)
  }

  static deleteCoupon(id: string): Promise<AxiosResponse> {
    return apiService.delete<AxiosResponse>(`/coupons/${id}`)
  }

  static deleteSlider(id: string): Promise<AxiosResponse> {
    return apiService.delete<AxiosResponse>(`/sliders/${id}`)
  }
}
